<script lang="ts" setup>
import {ChatBubbleLeftEllipsisIcon, PaperClipIcon, XMarkIcon} from "@heroicons/vue/24/outline";
import {ref} from "vue";
import MessageService from '@src/services/message'
import {useAuthStore} from "@src/stores/auth";
import Button from "@src/components/ui/inputs/Button.vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import {Toast} from "@src/plugins/toast";

const comment = ref("");
const attachedFiles = ref<File[]>([]);
const fileInput = ref<HTMLInputElement | null>(null);
const authStore = useAuthStore()
const loading = ref(false)
const sessionStore = useChatSessionStore()
const emits = defineEmits(['closeModal'])
const limit = ref(false)

function handleFileChange(event: Event) {
    if (attachedFiles.value.length === 4) {
        limit.value = true
        return Toast.fire("Limite de arquivos atingido!", "", "error")
    }
    const target = event.target as HTMLInputElement;
    if (target.files) {
        attachedFiles.value.push(...Array.from(target.files));
    }
}

function removeFile(index: number) {
    limit.value = false
    attachedFiles.value.splice(index, 1);
}

function triggerFileInput() {
    fileInput.value?.click();
}

async function submit() {
    loading.value = true
    const form = new FormData()
    form.append('message', comment.value)
    form.append('sessionId', sessionStore.selectedSession?.id.toString() || '')
    form.append('senderId', authStore.user?.id.toString() || '')
    if (attachedFiles.value && attachedFiles.value.length > 0) {
        attachedFiles.value.forEach((file: File, index: number) => {
            form.append(`files[${index}]`, file)
        })
    }
    await MessageService.submitComment(form)
    sessionStore.openOptionsSelector = false
    loading.value = false
    await sessionStore.scrollDown()
    emits('closeModal')
}
</script>

<template>
    <div class="fixed z-50 inset-0 overflow-y-auto" role="dialog" @click.stop>
        <div
            class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0 backdrop-blur-[2px]">
            <div class="fixed inset-0 opacity-25 bg-gray-500 dark:bg-gray-400 transition-opacity"
                 @click="$emit('closeModal')"></div>
            <div
                class="inline-block z-20 bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full md:max-w-3xl">
                <div class="bg-white dark:bg-gray-800 p-4 min-w-120 max-w-160">
                    <div class="sm:flex sm:items-start">
                        <div class="mt-3 sm:mt-0 w-full sm:text-left">
                            <div class="flex flex-row ">
                                <ChatBubbleLeftEllipsisIcon class="w-6 h-6 mr-3 text-gray-900 dark:text-gray-100"/>
                                <h3 class="text-lg leading-6 text-gray-900 dark:text-gray-100">
                                    Adicionar Comentário
                                </h3>
                            </div>
                            <div class="text-xs mt-2 text-gray-600 mb-6 dark:text-gray-200 text-left">
                                Comentários são visíveis somente para os atendentes e <strong>não são enviados ao
                                cliente</strong> como mensagem.
                            </div>
                            <div class="mb-4">
                                <label class="block text-md font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Comentário:
                                </label>
                                <textarea
                                    v-model="comment"
                                    class="border w-full min-h-20 rounded p-2 px-3 dark:text-gray-100"
                                    placeholder="Digite o comentário"
                                ></textarea>
                            </div>
                            <div class="mb-2">
                                <label class="block text-md font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Anexos:
                                </label>
                                <button
                                    :class="limit? 'cursor-not-allowed' : 'cursor-pointer'"
                                    :disabled="limit"
                                    class="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-indigo-500 hover:text-indigo-600 dark:hover:border-indigo-500 dark:hover:text-indigo-400 transition-colors bg-white dark:bg-gray-700"
                                    type="button"
                                    @click="triggerFileInput"
                                >
                                    <PaperClipIcon class="w-4 h-4"/>
                                    Anexar arquivos
                                </button>
                                <input
                                    v-if="!limit"
                                    ref="fileInput"
                                    accept="application/pdf, video/mp4, audio/mp3, image/png, image/jpeg, image/jpg"
                                    class="hidden"
                                    multiple
                                    type="file"
                                    @change="handleFileChange"
                                />

                                <ul v-if="attachedFiles.length" class="mt-2 space-y-2">
                                    <li
                                        v-for="(file, index) in attachedFiles"
                                        :key="index"
                                        class="flex items-center justify-between text-sm px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                    >
                                        <div class="flex items-center gap-2 truncate">
                                            <PaperClipIcon class="w-3.5 h-3.5 flex-shrink-0 text-gray-400"/>
                                            <span class="truncate">{{ file.name }}</span>
                                        </div>
                                        <button
                                            class="ml-2 flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                                            type="button"
                                            @click="removeFile(index)"
                                        >
                                            <XMarkIcon class="w-4 h-4"/>
                                        </button>
                                    </li>
                                </ul>
                            </div>

                        </div>
                    </div>
                </div>
                <div class="bg-gray-50 dark:bg-gray-700 px-4 py-6 flex justify-between">
                    <button
                        class="text-sm justify-center cursor-pointer rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-3 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-500"
                        type="button"
                        @click="$emit('closeModal')"
                    >
                        Fechar
                    </button>

                    <Button
                        :disabled="!comment"
                        :loading="loading"
                        class="disabled:opacity-25 cursor-pointer disabled:cursor-auto text-sm justify-center rounded-md px-4 py-3 bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                        type="button"
                        @click="submit"
                    >
                        Adicionar
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
</style>